import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { HandCoins, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import PlayerNameLink from '../PlayerNameLink';
import MarkRepaidDialog from './MarkRepaidDialog';
import { useLoanOwed } from '../../contexts/LoanOwedContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtCT = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

const OutstandingLoansCard = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const { refresh: refreshLoanMap } = useLoanOwed();

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/loans/outstanding`, { withCredentials: true });
            setRows(data.rows || []);
        } catch { /* silent */ }
        setLoading(false);
    }, []);
    useEffect(() => { fetchRows(); }, [fetchRows]);

    const handleRepaymentDone = useCallback(() => {
        fetchRows();
        refreshLoanMap();
    }, [fetchRows, refreshLoanMap]);

    const total = rows.reduce((s, r) => s + r.outstanding, 0);

    return (
        <div className="card-dark p-4" data-testid="outstanding-loans-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                    <h3 className="text-white text-base font-black flex items-center gap-2">
                        <HandCoins className="w-4 h-4 text-orange-400" />
                        Outstanding Loans
                    </h3>
                    <p className="text-[10px] text-[#A0AAB2] mt-0.5">
                        Players who still owe you money &middot; total outstanding <span className="text-orange-300 font-bold font-mono">{fmtMoney(total)}</span>
                    </p>
                </div>
                <button onClick={fetchRows} disabled={loading}
                    className="p-1.5 rounded-lg border border-white/10 hover:bg-white/5"
                    data-testid="outstanding-loans-refresh">
                    <RefreshCw className={`w-4 h-4 text-[#A0AAB2] ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {loading && rows.length === 0 ? (
                <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-5 h-5 text-orange-400 animate-spin" />
                </div>
            ) : rows.length === 0 ? (
                <p className="text-center text-[#A0AAB2] py-8 text-sm">All loans repaid &middot; nothing outstanding 🎉</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-[10px] text-[#A0AAB2] uppercase tracking-widest">
                                <th className="px-2 py-1.5">Player</th>
                                <th className="px-2 py-1.5 text-right">Loaned</th>
                                <th className="px-2 py-1.5 text-right">Repaid</th>
                                <th className="px-2 py-1.5 text-right">Outstanding</th>
                                <th className="px-2 py-1.5">Last loan</th>
                                <th className="px-2 py-1.5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.user_id} className="border-t border-white/5" data-testid={`outstanding-loan-${r.user_id}`}>
                                    <td className="px-2 py-2">
                                        <PlayerNameLink id={r.user_id} name={r.name} hideOwedBadge />
                                        <div className="text-[10px] text-[#666]">{r.phone || r.email}</div>
                                    </td>
                                    <td className="px-2 py-2 text-right font-mono text-[#A0AAB2]">
                                        ${r.loaned.toFixed(2)}
                                        <span className="block text-[9px] text-[#666]">{r.loan_count} loan{r.loan_count === 1 ? '' : 's'}</span>
                                    </td>
                                    <td className="px-2 py-2 text-right font-mono text-emerald-400">${r.repaid.toFixed(2)}</td>
                                    <td className="px-2 py-2 text-right font-mono font-black text-orange-300 text-base">${r.outstanding.toFixed(2)}</td>
                                    <td className="px-2 py-2 text-[10px] text-[#A0AAB2]">{fmtCT(r.last_loan_at)}</td>
                                    <td className="px-2 py-2 text-right">
                                        <Button size="sm"
                                            onClick={() => setActive(r)}
                                            className="bg-orange-500/20 border border-orange-500/40 text-orange-300 hover:bg-orange-500/30 h-7 text-[10px] font-black"
                                            data-testid={`mark-repaid-btn-${r.user_id}`}
                                        >
                                            Mark Repaid
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <MarkRepaidDialog open={!!active} row={active} onClose={() => setActive(null)} onDone={handleRepaymentDone} />
        </div>
    );
};

export default OutstandingLoansCard;
